import {isEmpty} from 'lodash-es';
import React from 'react';
import {useParams} from 'react-router-dom';

import {Header, SpinningLoader} from '@/components';
import DeactivateAccount from '@/components/DeactivateAccount';
import SecurityView from '@/components/SecurityNotes';
import {AfriexVerificationStatus} from '@/types';

import TierInformation from './TierInformationProfile';
import TransactionLimits from './TransactionLimits';
import TransactionsView from './TransactionsView';
import UserOverview from './UserOverview';
import useUserDetails from './useUserDetails';
import VirtualBankAccounts from './VirtualBankAccounts';

const UserDetails: React.FC = () => {
  const params = useParams();
  const userId = params?.userId || '';
  const {
    isAddingSecurityNote,
    isLoadingWalletBalances,
    isLoadingSecurityNotes,
    isLoadingUserDetails,
    securityNotes,
    hasMoreNotes,
    isFetchingMore,
    loadMoreSecurityNotes,
    addSecurityNote,
    showModal,
    toggleModal,
    user,
    walletBalances,
    toggleComplianceModal,
    showComplianceModal,
    fetchUserDetails,
    virtualBankAccounts,
  } = useUserDetails(userId);
  const {tierInfo} = user;
  const isVerified = user?.kyc?.status === AfriexVerificationStatus.Success;

  if (isLoadingUserDetails || isEmpty(user)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <SpinningLoader />
      </div>
    );
  }

  return (
    <main className="mt-10 mb-5 mx-4 md:mx-10">
      <Header title="User Details" />

      {/* User Profile */}
      <UserOverview
        isLoadingUserDetails={isLoadingUserDetails}
        showModal={showModal}
        toggleModal={toggleModal}
        user={user}
        toggleComplianceModal={toggleComplianceModal}
        showComplianceModal={showComplianceModal}
        fetchUserDetails={fetchUserDetails}
      />

      {/* Security */}
      <SecurityView
        addSecurityNote={addSecurityNote}
        isAddingSecurityNote={isAddingSecurityNote}
        securityNotes={securityNotes}
        isLoadingSecurityNotes={isLoadingSecurityNotes}
        user={user}
        hasMoreNotes={hasMoreNotes}
        loadMoreSecurityNotes={loadMoreSecurityNotes}
        isFetchingMore={isFetchingMore}
      />

      {/* Transactions */}
      <TransactionsView
        user={user}
        walletBalances={walletBalances}
        isLoadingWalletBalances={isLoadingWalletBalances}
      />

      {/*Transaction Limits */}
      <TransactionLimits
        isVerified={isVerified}
        dailyLimit={user?.limits?.perDailyLimit}
        perTransactionLimit={user?.limits?.perTransactionLimit}
        country={user?.currentCountry}
      />

      {/* Virtual Bank Accounts */}
      <VirtualBankAccounts accounts={virtualBankAccounts || []} />

      {/* Promo Tiers */}
      <TierInformation
        id={tierInfo?.id}
        transactionCount={tierInfo?.transactionCount}
        userId={user?.id}
        refetchUser={fetchUserDetails}
      />

      {/* Deactivate User */}
      <DeactivateAccount user={user} />
    </main>
  );
};

export default UserDetails;
